import { Recipe } from '../Types/api';

/**
 * Generate a deterministic hash from a string seed.
 * Same input always produces same output.
 */
export function seededHash(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get today's date in YYYY-MM-DD format.
 */
export function getTodayDateString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * Get a deterministic daily recipe suggestion.
 * Same user + date + skipCount always returns the same recipe.
 *
 * @param recipes - List of available recipes to choose from
 * @param userId - User's unique ID
 * @param date - Date string in YYYY-MM-DD format
 * @param skipCount - Number of times user clicked "Try Another" (default 0)
 * @returns The suggested recipe, or null if no recipes available
 */
export function getDailySuggestion(
  recipes: Recipe[],
  userId: string,
  date: string,
  skipCount = 0,
): Recipe | null {
  if (!recipes || recipes.length === 0) {
    return null;
  }

  // Create a unique seed from userId, date, and skipCount
  const seed = `${userId}-${date}-${skipCount}`;
  const hash = seededHash(seed);
  const index = hash % recipes.length;

  return recipes[index];
}

/**
 * localStorage key for storing the skip count per date.
 */
const SKIP_COUNT_KEY = 'todaysRecipe_skipCount';

interface SkipCountData {
  date: string;
  count: number;
}

/**
 * Get the current skip count for today from localStorage.
 */
export function getSkipCount(): number {
  try {
    const stored = localStorage.getItem(SKIP_COUNT_KEY);
    if (!stored) return 0;

    const data: SkipCountData = JSON.parse(stored);
    const today = getTodayDateString();

    // Reset if it's a new day
    if (data.date !== today) {
      return 0;
    }

    return data.count;
  } catch {
    return 0;
  }
}

/**
 * Increment and save the skip count for today.
 * Returns the new skip count.
 */
export function incrementSkipCount(): number {
  const today = getTodayDateString();
  const currentCount = getSkipCount();
  const newCount = currentCount + 1;

  const data: SkipCountData = {
    date: today,
    count: newCount,
  };

  try {
    localStorage.setItem(SKIP_COUNT_KEY, JSON.stringify(data));
  } catch {
    // localStorage might be full or disabled, ignore
  }

  return newCount;
}

/**
 * Reset the skip count (used when testing or manually resetting).
 */
export function resetSkipCount(): void {
  try {
    localStorage.removeItem(SKIP_COUNT_KEY);
  } catch {
    // Ignore errors
  }
}
